import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Popconfirm,
  Row,
  Select,
  Statistic,
  Tag,
  Typography,
  message,
} from "antd";
import { DatabaseOutlined, DeleteOutlined, FolderOutlined, SaveOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { api } from "../api";
import { useRole } from "../contexts/AuthContext";

const { Text, Title } = Typography;

// ── retention options ─────────────────────────────────────────────────────────

const RETENTION_OPTIONS = [
  { label: "7 days",   value: 7 },
  { label: "30 days",  value: 30 },
  { label: "90 days",  value: 90 },
  { label: "180 days", value: 180 },
  { label: "1 year",   value: 365 },
  { label: "2 years",  value: 730 },
  { label: "5 years",  value: 1827 },
  { label: "Infinite", value: 0 },
];

// ── wgflow retention options ──────────────────────────────────────────────────

const WGFLOW_RETENTION_OPTIONS = [
  { label: "1 day",      value: 1 },
  { label: "3 days",     value: 3 },
  { label: "7 days",     value: 7 },
  { label: "14 days",    value: 14 },
  { label: "30 days",    value: 30 },
  { label: "60 days",    value: 60 },
  { label: "Disabled",   value: 0 },
];

const WGFLOW_FLUSH_OPTIONS = [
  { label: "Older than 1 day",   value: 1 },
  { label: "Older than 3 days",  value: 3 },
  { label: "Older than 7 days",  value: 7 },
  { label: "Older than 14 days", value: 14 },
  { label: "Older than 30 days", value: 30 },
];

function fmtBytes(b: number): string {
  if (b >= 1e9) return (b / 1e9).toFixed(1) + " GB";
  if (b >= 1e6) return (b / 1e6).toFixed(1) + " MB";
  if (b >= 1e3) return (b / 1e3).toFixed(1) + " KB";
  return b + " B";
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ── flush options: "delete data older than X" ─────────────────────────────────

const FLUSH_OPTIONS = [
  { label: "All data (start fresh)",  value: 0 },
  { label: "Older than 1 day",        value: 86400 },
  { label: "Older than 7 days",       value: 7 * 86400 },
  { label: "Older than 30 days",      value: 30 * 86400 },
  { label: "Older than 90 days",      value: 90 * 86400 },
  { label: "Older than 180 days",     value: 180 * 86400 },
  { label: "Older than 1 year",       value: 365 * 86400 },
];

function retentionLabel(days: number): string {
  if (days === 0) return "Infinite";
  if (days <= 7) return "7 days";
  if (days <= 30) return "30 days";
  if (days <= 90) return "90 days";
  if (days <= 180) return "180 days";
  if (days <= 365) return "1 year";
  if (days <= 730) return "2 years";
  return "5 years";
}

// ── page ──────────────────────────────────────────────────────────────────────

type WgflowDir = {
  path: string;
  file_count: number;
  total_bytes: number;
  oldest_file?: string;
  newest_file?: string;
};

export default function GraphsStorage() {
  const { isAdmin } = useRole();
  const [currentDays, setCurrentDays] = useState<number | null>(null);
  const [selectedDays, setSelectedDays] = useState<number>(1827);
  const [flushOlderThan, setFlushOlderThan] = useState<number>(0);
  const [loadingRetention, setLoadingRetention] = useState(true);
  const [savingRetention, setSavingRetention] = useState(false);
  const [flushing, setFlushing] = useState(false);

  // wgflow state
  const [wgflowDirs, setWgflowDirs] = useState<WgflowDir[]>([]);
  const [wgflowRetDays, setWgflowRetDays] = useState<number>(7);
  const [wgflowSelDays, setWgflowSelDays] = useState<number>(7);
  const [wgflowFlushDays, setWgflowFlushDays] = useState<number>(7);
  const [loadingWgflow, setLoadingWgflow] = useState(true);
  const [savingWgflow, setSavingWgflow] = useState(false);
  const [flushingWgflow, setFlushingWgflow] = useState(false);

  const loadRetention = useCallback(async () => {
    setLoadingRetention(true);
    try {
      const resp = await api.getRetention();
      setCurrentDays(resp.days);
      // Pre-select closest option
      const closest = RETENTION_OPTIONS.reduce((prev, cur) =>
        Math.abs(cur.value - resp.days) < Math.abs(prev.value - resp.days) ? cur : prev
      );
      setSelectedDays(closest.value);
    } catch (e) {
      if (e instanceof Error) message.error("Failed to load retention: " + e.message);
    } finally {
      setLoadingRetention(false);
    }
  }, []);

  const loadWgflow = useCallback(async () => {
    setLoadingWgflow(true);
    try {
      const resp = await api.wgflowStats();
      setWgflowDirs(resp.dirs);
      setWgflowRetDays(resp.retention_days);
      setWgflowSelDays(resp.retention_days);
    } catch (e) {
      if (e instanceof Error) message.error("Failed to load wgflow stats: " + e.message);
    } finally {
      setLoadingWgflow(false);
    }
  }, []);

  useEffect(() => { loadRetention(); loadWgflow(); }, [loadRetention, loadWgflow]);

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      await api.setRetention(selectedDays);
      message.success(`Retention set to ${retentionLabel(selectedDays)}`);
      setCurrentDays(selectedDays === 0 ? 1827 : selectedDays);
    } catch (e) {
      if (e instanceof Error) message.error("Failed: " + e.message);
    } finally {
      setSavingRetention(false);
    }
  };

  const saveWgflowRetention = async () => {
    setSavingWgflow(true);
    try {
      await api.wgflowSetRetention(wgflowSelDays);
      setWgflowRetDays(wgflowSelDays);
      message.success(wgflowSelDays === 0
        ? "wgflow auto-cleanup disabled"
        : `wgflow retention set to ${wgflowSelDays} day${wgflowSelDays !== 1 ? "s" : ""}`);
    } catch (e) {
      if (e instanceof Error) message.error("Failed: " + e.message);
    } finally {
      setSavingWgflow(false);
    }
  };

  const doWgflowFlush = async () => {
    setFlushingWgflow(true);
    try {
      const r = await api.wgflowFlush(wgflowFlushDays);
      message.success(`Deleted ${r.deleted_files.toLocaleString()} files, freed ${fmtBytes(r.freed_bytes)}`);
      loadWgflow();
    } catch (e) {
      if (e instanceof Error) message.error("Flush failed: " + e.message);
    } finally {
      setFlushingWgflow(false);
    }
  };

  const doFlush = async () => {
    setFlushing(true);
    try {
      await api.flushData(flushOlderThan);
      const label = FLUSH_OPTIONS.find(o => o.value === flushOlderThan)?.label ?? "";
      message.success(`Flushed: ${label}`);
    } catch (e) {
      if (e instanceof Error) message.error("Flush failed: " + e.message);
    } finally {
      setFlushing(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: "#2a3545" }}>
        Graphs &amp; Storage
      </Title>

      {/* Current status */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Current max data age"
              value={currentDays == null ? "…" : retentionLabel(currentDays)}
              valueStyle={{ fontSize: 16, color: "#3a5a90" }}
              loading={loadingRetention}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Raw data (5 s bins)"
              value="25 hours"
              valueStyle={{ fontSize: 16, color: "#555" }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Storage backend"
              value="InfluxDB 1.x"
              valueStyle={{ fontSize: 16, color: "#555" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Retention settings */}
      <Card
        size="small"
        title={<span style={{ fontWeight: 700 }}>Data Retention</span>}
        style={{ marginBottom: 16 }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Sets the maximum age of stored data. Older data is automatically purged by
          InfluxDB retention policies. Raw 5-second data is always kept for 25 hours
          regardless of this setting.
        </Text>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
          <Select
            value={selectedDays}
            onChange={setSelectedDays}
            options={RETENTION_OPTIONS}
            style={{ width: 160 }}
            size="middle"
            disabled={!isAdmin}
          />
          {isAdmin && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveRetention}
              loading={savingRetention}
            >
              Save Retention
            </Button>
          )}
        </div>

        {currentDays !== null && selectedDays !== currentDays && selectedDays !== 0 && selectedDays < currentDays && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message={`This will reduce retention from ${retentionLabel(currentDays)} to ${retentionLabel(selectedDays)}. Data older than ${retentionLabel(selectedDays)} will be automatically deleted by InfluxDB.`}
          />
        )}
      </Card>

      {isAdmin && <>
      <Divider style={{ margin: "4px 0 16px" }} />

      {/* Flush data */}
      <Card
        size="small"
        title={
          <span style={{ fontWeight: 700, color: "#c0392b" }}>
            <DeleteOutlined style={{ marginRight: 6 }} />
            Flush Data
          </span>
        }
        style={{ borderColor: "#f5c6cb" }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Immediately delete time-series data from InfluxDB. This action cannot be undone.
          The system continues collecting new data normally after flush.
        </Text>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
          <Select
            value={flushOlderThan}
            onChange={setFlushOlderThan}
            options={FLUSH_OPTIONS}
            style={{ width: 220 }}
            size="middle"
          />
          <Popconfirm
            title="Flush data?"
            description={
              <span>
                {flushOlderThan === 0
                  ? "This will delete ALL time-series data. Are you sure?"
                  : `This will delete data older than ${FLUSH_OPTIONS.find(o => o.value === flushOlderThan)?.label?.replace("Older than ", "")}. Continue?`}
              </span>
            }
            okText="Yes, flush"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={doFlush}
          >
            <Button
              danger
              icon={<ThunderboltOutlined />}
              loading={flushing}
            >
              Flush Now
            </Button>
          </Popconfirm>
        </div>

        {flushOlderThan === 0 && (
          <Alert
            style={{ marginTop: 12 }}
            type="error"
            showIcon
            message="All data mode: this will wipe every data point from the database immediately."
          />
        )}
      </Card>
      </>}

      {/* ── Flow Archive Storage ─────────────────────────────────── */}
      <Divider style={{ margin: "20px 0 16px" }} />

      <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: "#2a3545" }}>
        <DatabaseOutlined style={{ marginRight: 8 }} />
        Flow Archive Storage
      </Title>

      {/* Disk usage stats */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        {loadingWgflow ? (
          <Col span={24}><Card size="small" loading /></Col>
        ) : wgflowDirs.map(dir => {
          const totalFiles = dir.file_count;
          const totalSize = fmtBytes(dir.total_bytes);
          return (
            <Col span={24} key={dir.path}>
              <Card size="small" style={{ marginBottom: 8 }}>
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <FolderOutlined style={{ color: "#888" }} />
                      <Text style={{ fontFamily: "monospace", fontSize: 12, color: "#555" }}>{dir.path}</Text>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Tag color="blue">{totalFiles.toLocaleString()} files</Tag>
                      <Tag color={dir.total_bytes > 10e9 ? "red" : dir.total_bytes > 5e9 ? "orange" : "green"}>
                        {totalSize}
                      </Tag>
                      {dir.oldest_file && (
                        <Tag>Oldest: {fmtDate(dir.oldest_file)}</Tag>
                      )}
                      {dir.newest_file && (
                        <Tag>Newest: {fmtDate(dir.newest_file)}</Tag>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* wgflow auto-delete retention */}
      <Card
        size="small"
        title={<span style={{ fontWeight: 700 }}>Auto-Delete (wgflow archives)</span>}
        style={{ marginBottom: 16 }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          wgflow.* binary archives are written every 5 minutes by sfsensor. Set a maximum
          age — files older than this are deleted automatically every hour.
          Set to <b>Disabled</b> to keep all files (not recommended on busy sensors).
        </Text>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
          <Select
            value={wgflowSelDays}
            onChange={setWgflowSelDays}
            options={WGFLOW_RETENTION_OPTIONS}
            style={{ width: 160 }}
            size="middle"
            disabled={!isAdmin}
          />
          {isAdmin && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveWgflowRetention}
              loading={savingWgflow}
            >
              Save
            </Button>
          )}
          {wgflowRetDays !== wgflowSelDays && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              (current: {wgflowRetDays === 0 ? "disabled" : `${wgflowRetDays}d`})
            </Text>
          )}
        </div>
        {wgflowSelDays === 0 && (
          <Alert style={{ marginTop: 12 }} type="warning" showIcon
            message="Auto-cleanup disabled — wgflow files will accumulate indefinitely." />
        )}
      </Card>

      {isAdmin && (
        <>
          <Divider style={{ margin: "4px 0 16px" }} />
          <Card
            size="small"
            title={
              <span style={{ fontWeight: 700, color: "#c0392b" }}>
                <DeleteOutlined style={{ marginRight: 6 }} />
                Flush wgflow Archives Now
              </span>
            }
            style={{ borderColor: "#f5c6cb" }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Immediately delete wgflow.* archive files older than the selected age.
              This cannot be undone. New flow data continues recording normally.
            </Text>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
              <Select
                value={wgflowFlushDays}
                onChange={setWgflowFlushDays}
                options={WGFLOW_FLUSH_OPTIONS}
                style={{ width: 200 }}
                size="middle"
              />
              <Popconfirm
                title="Delete wgflow archives?"
                description={`This will delete all wgflow.* files older than ${wgflowFlushDays} day${wgflowFlushDays !== 1 ? "s" : ""}. Continue?`}
                okText="Yes, delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                onConfirm={doWgflowFlush}
              >
                <Button danger icon={<ThunderboltOutlined />} loading={flushingWgflow}>
                  Flush Now
                </Button>
              </Popconfirm>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
